import {
	Button,
	CircularProgress,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { CacheConfiguration } from "../../models/CacheConfiguration";
import { deleteQueryCacheConfiguration } from "../../repositories/query-cache-repository";

type Props = {
	show: boolean;
	configuration: CacheConfiguration | null;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const DeleteCachedAction = ({
	show,
	configuration,
	onHide,
	suffixTitle,
}: Props) => {
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [error, setError] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const handleClose = (
		event?: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	const removeConfiguration = useMutation({
		mutationFn: deleteQueryCacheConfiguration,
		onSuccess: () => {
			setOpenSnackbar(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onDelete = async () => {
		if (!configuration) return;
		setIsSubmitLoading(true);
		await removeConfiguration.mutate(configuration.ActionId);
	};

	useEffect(() => {
		if (show) setIsSubmitLoading(false);
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Cached Action${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="DELETE CACHED ACTION"
				open={show}
				handleClose={() => onHide(false)}
				id="delete-cached-action-modal"
				maxWidth="md"
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={1}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Typography>
								<strong>{configuration?.ActionName}</strong> will stop being
								cached and will go to the database on every call. Continue?
							</Typography>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: 16 }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading}
								variant="contained"
								color="secondary"
								onClick={onDelete}
							>
								DELETE
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The cached action was deleted successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Deleting Cached Action"
			/>
		</>
	);
};
